import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Types (mirror backend/src/modules/team/team.service.ts DTOs)
// ---------------------------------------------------------------------------

export type PersonType = 'STAFF' | 'CONTRACTOR';
export type CostModel = 'HOURLY' | 'FLAT_MONTHLY';
/** How often a timesheet is expected. Mirrors `features/time/api.ts`. */
export type TimesheetCadence = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'NEVER';

export interface Person {
  id: string;
  name: string;
  email: string;
  type: PersonType;
  costModel: CostModel;
  hourlyCost: number | null;
  flatMonthlyCost: number | null;
  defaultBillRate: number | null;
  /** Contracted availability per week, in hours. null = no committed capacity. */
  weeklyCapacityHours: number | null;
  isActive: boolean;
  /**
   * How often a timesheet is expected from this person — the PERIOD their
   * sheet covers (a day, a week or a month) and so what the reminder chases.
   * `NEVER` whenever `tracksTime` is false; a tracker may also be `NEVER`
   * (logs hours, is not chased for a sheet).
   *
   * Replaces the old `submitsTimesheets` boolean.
   */
  timesheetCadence: TimesheetCadence;
  /**
   * Is submitting REQUIRED, or only possible? Off = they may submit, but a
   * period they have not sent is never late or reminded. Off for contractors
   * by default.
   */
  timesheetRequired: boolean;
  /**
   * Does this person log hours at all?
   *
   * The tag that decides where a timer is OFFERED — the Solid app bar in every
   * product, and the hub's time UI. NOT the same as `submitsTimesheets`: that
   * is "a weekly sheet is expected", this is "logs hours". Defaults FALSE for
   * anyone new, so somebody arriving from the roster is never assumed to be a
   * tracker.
   */
  tracksTime: boolean;
  /**
   * Linked to a Solid account. Without one the timer can never appear for this
   * person — the door refuses them and the widget renders nothing — so
   * `tracksTime` on its own is a promise the app cannot keep.
   */
  hasSolidAccount: boolean;
  /** Where they work from. Required of everyone who tracks time. */
  address: PersonAddress;
  /**
   * Complete enough to file — street, city and country, plus state and ZIP in
   * the US (server-decided, `person-address.ts`). Tracking cannot be turned on
   * without it; a tracker missing one is flagged.
   */
  hasAddress: boolean;
  startDate: string | null;
  endDate: string | null;
}

/** Mirrors backend `team/person-address.ts`. `country` is ISO 3166-1 alpha-2. */
export interface PersonAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface PersonMonthStats {
  month: string;
  hours: number;
  billableHours: number;
  /** Available hours for the period (weekly capacity × business days ÷ 5). */
  capacityHours: number;
  /** billable ÷ capacity — null when the person has no capacity set */
  utilization: number | null;
  /** billable ÷ tracked — null when nothing was tracked */
  billableShare: number | null;
  /** billable hours valued at their effective bill rate */
  revenue: number;
  cost: number;
  /** revenue − cost */
  margin: number;
  /** month still running: capacity and cost are to-date */
  inProgress: boolean;
}

export type TeamPerson = Person & { currentMonth: PersonMonthStats };

export interface TeamListResponse {
  month: string;
  latestMonth: string;
  inProgress: boolean;
  people: TeamPerson[];
}

export interface OrgUtilizationMonth {
  month: string;
  totalHours: number;
  billableHours: number;
  /**
   * **UTILIZATION — billable ÷ CAPACITY.** The word, and the only ratio the
   * 75% target judges. `/dashboard` prints this same number for STAFF.
   */
  utilization: number | null;
  capacityHours: number;
  /**
   * **BILLABLE SHARE — billable ÷ TRACKED.** A timesheet diagnostic with no
   * target; never label it "utilization" and never put a target beside it.
   */
  billableShare: number | null;
  peopleTracking: number;
  avgHoursPerPersonPerWeek: number | null;
}

/** Population an org utilization series covers. */
export type UtilizationScope = 'ALL' | 'STAFF' | 'CONTRACTOR';

export interface OrgUtilizationResponse {
  scope: UtilizationScope;
  months: OrgUtilizationMonth[];
  current: OrgUtilizationMonth;
  activeStaff: number;
  activeContractors: number;
  /** Active people inside `scope`. */
  activeInScope: number;
}

export interface PersonMonthRow {
  month: string;
  hours: number;
  billableHours: number;
  capacityHours: number;
  utilization: number | null;
  billableShare: number | null;
  revenue: number;
  cost: number;
  margin: number;
  inProgress: boolean;
}

/** One month of one person — opened from a row of the monthly table. */
export interface PersonMonthDetail {
  person: { id: string; name: string; type: PersonType; costModel: CostModel };
  /** The row that was clicked, computed by the same code — the footing. */
  month: PersonMonthRow;
  /** `salary` — a flat monthly cost, not split by agreement or entry. */
  costBasis: 'hourly' | 'salary';
  uncostedHours: number;
  agreements: Array<{
    agreementId: string;
    agreementName: string;
    clientName: string;
    entries: number;
    hours: number;
    billableHours: number;
    revenue: number;
    cost: number | null;
  }>;
  entries: Array<{
    id: string;
    date: string;
    agreementId: string;
    agreementName: string;
    clientName: string;
    taskName: string | null;
    notes: string | null;
    hours: number;
    billable: boolean;
    invoiced: boolean;
    deletedInSource: boolean;
    running: boolean;
    billRate: number | null;
    revenue: number;
    costRate: number | null;
    cost: number | null;
  }>;
}

export interface PersonDetailResponse {
  person: Person;
  months: PersonMonthRow[];
  totals: {
    hours: number;
    billableHours: number;
    capacityHours: number;
    utilization: number | null;
    billableShare: number | null;
    revenue: number;
    cost: number;
    margin: number;
  };
  trailing3: { months: number; revenue: number; cost: number; margin: number };
  negativeMonthStreak: number;
  agreements: Array<{
    agreementId: string;
    agreementName: string;
    clientName: string;
    hours: number;
    billableHours: number;
    revenue: number;
  }>;
  recentEntries: Array<{
    id: string;
    date: string;
    agreementName: string;
    clientName: string;
    hours: number;
    billable: boolean;
    invoiced: boolean;
    notes: string | null;
  }>;
}

export interface PersonFormValues {
  name: string;
  email: string;
  type: PersonType;
  costModel: CostModel;
  hourlyCost: number | null;
  flatMonthlyCost: number | null;
  defaultBillRate: number | null;
  weeklyCapacityHours: number | null;
  /**
   * ⚠ The time policy (`tracksTime`, `timesheetCadence`) is deliberately NOT
   * here.
   *
   * This shape is the body of the generic PATCH, which is refused while
   * Harvest owns the row. The policy has to remain writable on exactly those
   * rows, so it travels on its own route (`useSetTimePolicy`) and never inside
   * this body.
   */
  startDate: string | null;
  endDate: string | null;
}

/**
 * `POST /team` — a person is only ever created FROM an accounts.soliddigital.com
 * account. Name and email are the account's (the server re-reads them), so they
 * are not in this body at all.
 */
export type NewPersonInput = Omit<PersonFormValues, 'name' | 'email'> & {
  authUserId: string;
  tracksTime?: boolean;
  timesheetCadence?: TimesheetCadence;
  timesheetRequired?: boolean;
  address?: PersonAddress;
};

/** One member of the organization in accounts.soliddigital.com. */
export interface AccountMember {
  userId: string;
  name: string;
  email: string;
  picture: string | null;
  orgRole: string | null;
  /** Set ⇒ already a person in financials, bound to this account. */
  personId: string | null;
  personName: string | null;
  personActive: boolean | null;
  /**
   * Set ⇒ an existing person with this email and NO account (a Harvest-era
   * row). Offer "Link", never "Add" — adding would make one human two people.
   */
  matchPersonId: string | null;
  matchPersonName: string | null;
}

export interface AccountRosterResponse {
  ok: boolean;
  /** Why the roster could not be read, in the server's words. */
  reason: string | null;
  members: AccountMember[];
}

export interface TimePolicyInput {
  tracksTime?: boolean;
  timesheetCadence?: TimesheetCadence;
  timesheetRequired?: boolean;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const teamKeys = {
  all: ['team'] as const,
  list: (month?: string) => [...teamKeys.all, 'list', month ?? 'current'] as const,
  utilization: (scope: UtilizationScope = 'STAFF') =>
    [...teamKeys.all, 'utilization', scope] as const,
  detail: (id: string) => [...teamKeys.all, 'detail', id] as const,
  month: (id: string, month: string) => [...teamKeys.all, 'detail', id, 'month', month] as const,
  accounts: () => [...teamKeys.all, 'accounts'] as const,
};

/**
 * The organization's members in accounts.soliddigital.com — the only place a
 * new person can come from. Fetched only while something needs it (the New
 * person picker, a Link account picker); never on page load.
 */
export function useAccountRoster(enabled: boolean) {
  return useQuery({
    queryKey: teamKeys.accounts(),
    queryFn: () => api.get<AccountRosterResponse>('/team/accounts'),
    enabled,
    staleTime: 30_000,
  });
}

/** @param month YYYY-MM; omit for the current month. */
export function useTeamList(month?: string) {
  return useQuery({
    queryKey: teamKeys.list(month),
    queryFn: () =>
      api.get<TeamListResponse>(month ? `/team?month=${encodeURIComponent(month)}` : '/team'),
    placeholderData: (prev) => prev,
  });
}

/**
 * Org utilization series. `scope` must match the population shown in the table
 * beneath the KPI cards — the headline numbers may never describe a different
 * set of people than the rows.
 */
export function useOrgUtilization(scope: UtilizationScope = 'STAFF') {
  return useQuery({
    queryKey: teamKeys.utilization(scope),
    queryFn: () =>
      api.get<OrgUtilizationResponse>(`/team/utilization?scope=${encodeURIComponent(scope)}`),
    placeholderData: (prev) => prev,
  });
}

export function usePersonDetail(id: string | undefined) {
  return useQuery({
    queryKey: teamKeys.detail(id ?? ''),
    queryFn: () => api.get<PersonDetailResponse>(`/team/${id}`),
    enabled: Boolean(id),
  });
}

/** @param month YYYY-MM — fetched only while a month is open. */
export function usePersonMonth(id: string | undefined, month: string | null) {
  return useQuery({
    queryKey: teamKeys.month(id ?? '', month ?? ''),
    queryFn: () => api.get<PersonMonthDetail>(`/team/${id}/months/${month}`),
    enabled: Boolean(id && month),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function useInvalidateTeam() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: teamKeys.all });
}

export function useCreatePerson() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: (values: NewPersonInput) => api.post<Person>('/team', values),
    onSuccess: invalidate,
  });
}

export function useUpdatePerson(id: string) {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: (values: Partial<PersonFormValues>) => api.patch<Person>(`/team/${id}`, values),
    onSuccess: invalidate,
  });
}

/**
 * @deprecated Prefer `useSetTimePolicy` (a cadence, not a boolean). Kept only
 * for callers outside features/team.
 *
 * Turn the weekly timesheet chase on or off for one person.
 *
 * Its own route, not a field on PATCH: the generic update is refused while
 * Harvest owns the row (`HarvestSourceGuard`), and Harvest has no such field to
 * own — see `TeamService.setTimesheetExpectation`. Routing it through PATCH
 * would 409 on exactly the deployment that needs it.
 */
export function useSetTimesheetExpectation() {
  const invalidate = useInvalidateTeam();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expected }: { id: string; expected: boolean }) =>
      api.post<Person>(`/team/${id}/timesheet-expectation`, { expected }),
    onSuccess: () => {
      invalidate();
      /**
       * ⚠ NARROW ON PURPOSE — the roster and the reminder audience, nothing
       * else under `['time']`. A blanket invalidation also refetches
       * `/time/people`, and `TimePage` gates the whole Approvals view on that
       * response (`viewer.canApprove`): the view unmounted and came back as a
       * skeleton on every toggle, so the row you had just acted on vanished
       * mid-click. Keys are `['time', <orgId>, <name>, …]`.
       */
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && isTimePolicyReader(query.queryKey),
      });
    },
  });
}

/**
 * Time screens that read a person's time policy: the approvals queue, the
 * reminder audience, the attention strip. ⚠ NARROW ON PURPOSE — never
 * `['time', org, 'people']`: `TimePage` gates the Approvals view on that
 * response, and refetching it unmounted the view mid-click.
 */
function isTimePolicyReader(queryKey: readonly unknown[]): boolean {
  return (
    queryKey[0] === 'time' &&
    ['roster', 'reminders', 'approvals', 'attention'].includes(String(queryKey[2]))
  );
}

/**
 * Set whether a person tracks time and how often a timesheet is expected.
 *
 * Its own route, not a field on PATCH: the generic update is refused while
 * Harvest owns the row, and Harvest has no notion of either — routing them
 * through PATCH would 409 on exactly the deployment that needs them. The server
 * refuses turning tracking on for a person with no Solid account (409).
 */
export function useSetTimePolicy() {
  const invalidate = useInvalidateTeam();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...policy }: TimePolicyInput & { id: string }) =>
      api.post<Person>(`/team/${id}/time-policy`, policy),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && isTimePolicyReader(query.queryKey),
      });
    },
  });
}

/**
 * Set where a person works from. Its own route — Harvest owns a mirrored
 * person's name and rates, not their address — and it must be saved BEFORE
 * turning tracking on, which the server refuses without one.
 */
export function useSetPersonAddress() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: ({ id, address }: { id: string; address: PersonAddress }) =>
      api.put<Person>(`/team/${id}/address`, address),
    onSuccess: invalidate,
  });
}

/** Bind an existing (account-less) person to an accounts.soliddigital.com account. */
export function useLinkAccount() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: ({ id, authUserId }: { id: string; authUserId: string }) =>
      api.post<Person>(`/team/${id}/link-account`, { authUserId }),
    onSuccess: invalidate,
  });
}

export function useSetPersonArchived() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.post<Person>(`/team/${id}/${archived ? 'archive' : 'restore'}`),
    onSuccess: invalidate,
  });
}
